import { ApplicationApi } from 'api';
import { infinityCacheTime } from 'api/ReactQueryOptions';
import { mapToAreas } from 'domain/user/User';
import { Area } from 'library/models/Area';

export const securityMatrixQuery = (api: ApplicationApi) => ({
	queryKey: userKeys.securityMatrix(),
	queryFn: async () => {
		const response = await api.getSecurityMatrix();
		return mapToAreas(response.data);
	},
	...infinityCacheTime<Area[]>(),
});

export const getSupportedLanguagesQuery = (api: ApplicationApi) => ({
	queryKey: userKeys.languages(),
	queryFn: async () => {
		const response = await api.getSupportedLanguageList();
		return response.data;
	},
	...infinityCacheTime<Record<string, string>>(),
});

export const searchBusinessPartnersQuery = (
	api: ApplicationApi,
	searchPhrase: string,
	qaAccessForm = false
) => ({
	queryKey: userKeys.searchBusinessPartners(
		searchPhrase.trim(),
		qaAccessForm
	),
	queryFn: async () => {
		const trimmed = searchPhrase.trim();
		if (trimmed.length < 3) {
			return [];
		}

		const response = qaAccessForm
			? await api.searchBusinessPartnersForQaAccess({
					prompt: trimmed,
				})
			: await api.searchBusinessPartners({
					prompt: trimmed,
				});

		return response.data;
	},
	enabled: searchPhrase.trim().length >= 3,
	staleTime: 800,
	retry: 0,
});

export const getSingleBusinessPartnerForQaAccessQuery = (
	api: ApplicationApi,
	customerNumber: number
) => ({
	queryKey: userKeys.singleBusinessPartner(customerNumber),
	queryFn: async () => {
		const response = await api.getSingleBusinessPartnerForQaAccess({
			customerNumber: customerNumber ?? undefined,
		});
		return response.data ?? null;
	},
	enabled: customerNumber != null && customerNumber > 0,
	staleTime: 800,
	retry: 0,
});

export const getUserByIdQuery = (
	api: ApplicationApi,
	userId: string | null
) => ({
	queryKey: userKeys.byId(userId),
	queryFn: async () => {
		if (!userId) {
			return null;
		}
		const response = await api.getUserById({ userId });
		return response.data ?? null;
	},
	enabled: Boolean(userId),
	staleTime: 800,
	retry: 0,
});

export const userKeys = {
	all: ['user'] as const,
	data: () => [...userKeys.all, 'data'] as const,
	securityMatrix: () => [...userKeys.all, 'securityMatrix'] as const,
	menu: () => [...userKeys.all, 'menu'] as const,
	languages: () => [...userKeys.all, 'languages'] as const,
	searchBusinessPartners: (searchPhrase: string, qaAccessForm: boolean) =>
		[
			...userKeys.all,
			'businessPartners',
			searchPhrase,
			qaAccessForm,
		] as const,
	singleBusinessPartner: (customerNumber: number) =>
		[...userKeys.all, 'singleBusinessPartner', customerNumber] as const,
	byId: (userId: string | null) => [...userKeys.all, 'byId', userId] as const,
};
